import React, { useMemo, useState } from 'react';
import { useKanban } from '../context/KanbanContext';
import { ModalEditarTarea } from './ModalEditarTarea';
import { EmptyState } from './EmptyState';
import {
    TareaConRelaciones, EstadoTarea, Prioridad, ClaseServicio,
    ESTADO_LABELS, PRIORIDAD_LABELS, PRIORIDAD_COLORS, CLASE_SERVICIO_LABELS, CLASE_SERVICIO_ICONS, TIPO_TAREA_LABELS, TipoTarea
} from '../types';

type SortKey = 'titulo' | 'cliente' | 'estado' | 'prioridad' | 'tamanio' | 'fecha_limite' | 'creado_en' | 'responsable' | 'wip_aging';

const diasEnColumna = (iso: string): number => {
    const diff = Date.now() - new Date(iso).getTime();
    return Math.floor(diff / 86400000);
};

interface SortSpec {
    key: SortKey;
    dir: 'asc' | 'desc';
}

const PRIORIDAD_SORT: Record<Prioridad, number> = { urgente: 0, alta: 1, moderada: 2, baja: 3 };

const prioridadBadge = (p: Prioridad): string => {
    const c = PRIORIDAD_COLORS[p];
    return `${c.bg} ${c.text}`;
};
const ESTADO_SORT: Record<EstadoTarea, number> = {
    en_progreso: 0, esperando_cliente: 1, testing: 2, por_hacer: 3, backlog: 4, produccion: 5
};

const prioridadColor = (p: Prioridad): string => {
    if (p === 'urgente') return 'bg-red-100 text-red-700';
    if (p === 'alta') return 'bg-orange-100 text-orange-700';
    if (p === 'moderada') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-600';
};

const isOverdue = (fecha: string | null, estado: EstadoTarea): boolean => {
    if (!fecha || estado === 'produccion') return false;
    return new Date(fecha) < new Date(new Date().toDateString());
};

const formatFecha = (iso: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const VistaTabla: React.FC = () => {
    const { swimlanes, actualizarTarea, eliminarTarea, marcarCompletado } = useKanban();
    const [sort, setSort] = useState<SortSpec>({ key: 'creado_en', dir: 'desc' });
    const [filtro, setFiltro] = useState('');
    const [estadoFiltro, setEstadoFiltro] = useState<EstadoTarea | 'todos' | 'activos'>('activos');
    const [selectedTarea, setSelectedTarea] = useState<TareaConRelaciones | null>(null);

    const tareasFlat: TareaConRelaciones[] = useMemo(() => {
        const all: TareaConRelaciones[] = [];
        swimlanes.forEach(sw => {
            Object.values(sw.tareas).forEach(arr => arr.forEach(t => all.push(t)));
        });
        return all;
    }, [swimlanes]);

    const tareasFiltradas = useMemo(() => {
        let result = tareasFlat;

        if (estadoFiltro === 'activos') {
            result = result.filter(t => !t.archivado);
        } else if (estadoFiltro === 'todos') {
            // nada
        } else {
            result = result.filter(t => t.estado === estadoFiltro && !t.archivado);
        }

        if (filtro.trim()) {
            const q = filtro.toLowerCase();
            result = result.filter(t =>
                t.titulo.toLowerCase().includes(q) ||
                t.cliente_nombre.toLowerCase().includes(q) ||
                t.empresa.toLowerCase().includes(q) ||
                (t.responsable || '').toLowerCase().includes(q) ||
                (t.descripcion || '').toLowerCase().includes(q)
            );
        }

        const sorted = [...result].sort((a, b) => {
            let cmp = 0;
            switch (sort.key) {
                case 'titulo':       cmp = a.titulo.localeCompare(b.titulo); break;
                case 'cliente':      cmp = a.empresa.localeCompare(b.empresa); break;
                case 'estado':       cmp = ESTADO_SORT[a.estado] - ESTADO_SORT[b.estado]; break;
                case 'prioridad':    cmp = PRIORIDAD_SORT[a.prioridad] - PRIORIDAD_SORT[b.prioridad]; break;
                case 'tamanio':      cmp = a.tamanio.localeCompare(b.tamanio); break;
                case 'fecha_limite': {
                    const av = a.fecha_limite ? new Date(a.fecha_limite).getTime() : Infinity;
                    const bv = b.fecha_limite ? new Date(b.fecha_limite).getTime() : Infinity;
                    cmp = av - bv;
                    break;
                }
                case 'wip_aging': {
                    const av = a.archivado ? -1 : diasEnColumna(a.actualizado_en);
                    const bv = b.archivado ? -1 : diasEnColumna(b.actualizado_en);
                    cmp = av - bv;
                    break;
                }
                case 'creado_en':    cmp = new Date(a.creado_en).getTime() - new Date(b.creado_en).getTime(); break;
                case 'responsable':  cmp = (a.responsable || '').localeCompare(b.responsable || ''); break;
            }
            return sort.dir === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [tareasFlat, filtro, estadoFiltro, sort]);

    const handleSort = (key: SortKey) => {
        setSort(prev => prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: 'asc' }
        );
    };

    const SortHeader: React.FC<{ k: SortKey; children: React.ReactNode; align?: 'left' | 'right' }> = ({ k, children, align = 'left' }) => {
        const isActive = sort.key === k;
        return (
            <th
                onClick={() => handleSort(k)}
                className={`px-3 py-2.5 text-${align} text-[11px] font-semibold uppercase tracking-wider text-slate-600 cursor-pointer hover:bg-slate-100 select-none`}
            >
                <span className="inline-flex items-center gap-1">
                    {children}
                    {isActive && <span className="text-slate-900">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                </span>
            </th>
        );
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
                <input
                    type="text"
                    value={filtro}
                    onChange={e => setFiltro(e.target.value)}
                    placeholder="Buscar por título, cliente, responsable…"
                    className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                    value={estadoFiltro}
                    onChange={e => setEstadoFiltro(e.target.value as any)}
                    className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg bg-white"
                >
                    <option value="activos">Solo activos</option>
                    <option value="todos">Todos (incl. archivados)</option>
                    <option value="backlog">Backlog</option>
                    <option value="por_hacer">Por Hacer</option>
                    <option value="en_progreso">En Progreso</option>
                    <option value="esperando_cliente">Esperando Cliente</option>
                    <option value="testing">Testing</option>
                    <option value="produccion">Producción</option>
                </select>
                <span className="text-xs text-slate-500 font-medium">
                    {tareasFiltradas.length} tarea{tareasFiltradas.length !== 1 ? 's' : ''}
                </span>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <SortHeader k="titulo">Título</SortHeader>
                            <SortHeader k="cliente">Cliente</SortHeader>
                            <SortHeader k="estado">Estado</SortHeader>
                            <SortHeader k="prioridad">Prioridad</SortHeader>
                            <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tipo</th>
                            <SortHeader k="tamanio">Tamaño</SortHeader>
                            <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600">Clase</th>
                            <SortHeader k="responsable">Responsable</SortHeader>
                            <SortHeader k="fecha_limite">Fecha Límite</SortHeader>
                            <SortHeader k="wip_aging">⏱ WIP</SortHeader>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {tareasFiltradas.length === 0 && (
                            <tr>
                                <td colSpan={10} className="p-0">
                                    <EmptyState
                                        icon="🔍"
                                        title="Sin tareas que coincidan"
                                        description={estadoFiltro !== 'activos'
                                            ? 'Probá cambiar el filtro de estado o limpiar la búsqueda.'
                                            : 'No hay tareas con los criterios actuales.'}
                                        variant="compact"
                                    />
                                </td>
                            </tr>
                        )}
                        {tareasFiltradas.map(t => (
                            <tr
                                key={t.id}
                                onClick={() => setSelectedTarea(t)}
                                className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                            >
                                <td className="px-3 py-2.5">
                                    <div className="flex items-start gap-2">
                                        <span
                                            className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: t.cliente_color }}
                                        />
                                        <div>
                                            <div className="font-medium text-slate-900 line-clamp-1">{t.titulo}</div>
                                            {t.bloqueado && (
                                                <div className="text-[10px] text-red-600 font-medium">🚫 {t.motivo_bloqueo || 'Bloqueado'}</div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-3 py-2.5 text-slate-600">
                                    <div className="line-clamp-1">{t.empresa}</div>
                                    <div className="text-[10px] text-slate-400">{t.nombre_proyecto}</div>
                                </td>
                                <td className="px-3 py-2.5">
                                    <span className="text-xs font-medium text-slate-700">
                                        {ESTADO_LABELS[t.estado]}
                                    </span>
                                </td>
                                <td className="px-3 py-2.5">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${prioridadBadge(t.prioridad as Prioridad)}`}>
                                        <span>{PRIORIDAD_COLORS[t.prioridad as Prioridad].icon}</span>
                                        {PRIORIDAD_LABELS[t.prioridad as Prioridad]}
                                    </span>
                                </td>
                                <td className="px-3 py-2.5 text-xs text-slate-600">
                                    {TIPO_TAREA_LABELS[t.tipo_tarea as TipoTarea]}
                                </td>
                                <td className="px-3 py-2.5">
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-slate-100 text-xs font-bold text-slate-700">
                                        {t.tamanio}
                                    </span>
                                </td>
                                <td className="px-3 py-2.5 text-xs text-slate-600">
                                    {CLASE_SERVICIO_ICONS[t.clase_servicio as ClaseServicio]} {CLASE_SERVICIO_LABELS[t.clase_servicio as ClaseServicio]}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-slate-700">
                                    {t.responsable || <span className="text-slate-400 italic">sin asignar</span>}
                                </td>
                                <td className="px-3 py-2.5 text-xs">
                                    {t.fecha_limite ? (
                                        <span className={isOverdue(t.fecha_limite, t.estado) ? 'text-red-600 font-semibold' : 'text-slate-700'}>
                                            {formatFecha(t.fecha_limite)}
                                            {isOverdue(t.fecha_limite, t.estado) && ' ⚠️'}
                                        </span>
                                    ) : (
                                        <span className="text-slate-400">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2.5 text-xs">
                                    {t.archivado ? (
                                        <span className="text-slate-300">—</span>
                                    ) : (() => {
                                        const d = diasEnColumna(t.actualizado_en);
                                        const cls = d > 14 ? 'bg-red-600 text-white'
                                            : d > 7  ? 'bg-red-500 text-white'
                                            : d > 3  ? 'bg-amber-100 text-amber-800'
                                            :          'bg-slate-100 text-slate-600';
                                        return (
                                            <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${cls}`}
                                                title={`Hace ${d} día(s) en esta columna`}
                                            >
                                                {(d > 7 ? '⚠ ' : '')}{d}d
                                            </span>
                                        );
                                    })()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selectedTarea && (
                <ModalEditarTarea
                    tarea={selectedTarea}
                    onClose={() => setSelectedTarea(null)}
                    onActualizar={actualizarTarea}
                    onEliminar={eliminarTarea}
                    onMarcarCompletado={marcarCompletado}
                />
            )}
        </div>
    );
};